import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { slide as Menu } from 'react-burger-menu';
import { Link, NavLink, useLocation } from 'react-router-dom';
import toastAlert from '../../js/Base/ToastAlert';
import { Card, Container, Form, Spinner, Image } from 'react-bootstrap';
import { Input } from 'reactstrap';
import { Drawer } from '@mui/material';
import { cloudNativeEnabledSelector, crowdstrikeEnabledSelector } from '../../store/selectors/licenseSelectors';

export default function AppMenu({ menuOpen, handleStateChange }) {
  // 點擊連結時關閉選單
  const handleLinkClick = () => {
    handleStateChange({ isOpen: false });
  };

  const buildPermissionMap = (authorityCodes) => {
    const map = {
      cloudnative: new Set(),
      crowdstrike: new Set(),
    };
  
    authorityCodes.forEach(code => {
      const [platform, cloud] = code.split("_");
      if (map[platform]) {
        map[platform].add(cloud);
      }
    });
  
    return map;
  };

  // Platforms 比對權限
  const authorityCodes = useSelector(state => state.businessMembers.authorityCodes);
  const availablePlatforms = useMemo(() => {
    const set = new Set();
  
    authorityCodes.forEach(code => {
      const [platform] = code.split("_");
      set.add(platform);
    });
  
    return set;
  }, [authorityCodes]);
  
  const hasCloudNative = availablePlatforms.has('cloudnative');
  const hasCrowdStrike = availablePlatforms.has('crowdstrike');
  const initialPlatform = hasCloudNative
  ? '0'
  : hasCrowdStrike
  ? '1'
  : '';

  const location = useLocation();
  const [platform, setPlatform] = useState(initialPlatform);

  // 如果路徑包含 /crowdstrike，初始化時也同步 platform
  useEffect(() => {
    if (location.pathname.startsWith('/crowdstrike')) {
      setPlatform('1');
    }
  }, [location.pathname]);

  const handlePlatformChange = (e) => {
    setPlatform(e.target.value);
  };

  const cloudNativeEnabled = useSelector(cloudNativeEnabledSelector);
  const crowdstrikeEnabled = useSelector(crowdstrikeEnabledSelector);

  const canUseCloudNative = hasCloudNative && cloudNativeEnabled;
  const canUseCrowdStrike = hasCrowdStrike && crowdstrikeEnabled;
  useEffect(() => {
    // 預設選項
    if (canUseCloudNative && !canUseCrowdStrike) {
      setPlatform('0');
    } else if (!canUseCloudNative && canUseCrowdStrike) {
      setPlatform('1');
    } else if (!canUseCloudNative && !canUseCrowdStrike) {
      setPlatform('');
    }
  }, [canUseCloudNative, canUseCrowdStrike]);

  // Clouds 比對權限
  const permissionMap = useMemo(
    () => buildPermissionMap(authorityCodes),
    [authorityCodes]
  );
  const allowedClouds = permissionMap[
    platform === '1' ? 'crowdstrike' : 'cloudnative'
  ] || new Set();

  // // 多雲合規總表 比對權限
  // const hasSummaryPermission = (authorityCodes, platformKey) => {
  //   return authorityCodes.some(code => {
  //     const [platform, , type] = code.split("_");
  //     return platform === platformKey && type === "summary";
  //   });
  // };
  // const platformKey = platform === '1' ? 'crowdstrike' : 'cloudnative';
  // const canViewSummary = hasSummaryPermission(authorityCodes, platformKey);

  // 連結前綴
  const prefix = platform === '1' ? '/crowdstrike' : '';

  return (
    <Drawer
      anchor="left"
      open={menuOpen}
      onClose={handleLinkClick}
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          width: 224,
          top: 50,
          height: 'calc(100% - 50px)',
        },
      }}
    >
      <div className="AppMenu">
        <div className="platform-select-wrapper">
          <Input
            type="select"
            className="platform-select"
            style={{ fontSize: '13px' }}
            id="platform"
            name="platform"
            value={platform}
            onChange={handlePlatformChange}
          >
            {canUseCloudNative && <option value="0">雲原生</option>}
            {canUseCrowdStrike && <option value="1">CrowdStrike</option>}
          </Input>
        </div>

        {/* CrowdStrike 尚無多雲合規總覽頁，先不顯示 */}
        {platform !== '1' && (
          <NavLink className="menu-item" to={`${prefix}/HomePage`}>
            <Image src="/assets/images/browse_24dp_323232_FILL0_wght200_GRAD0_opsz24_n.svg" />
            多雲合規性檢視
          </NavLink>
        )}

        {allowedClouds.has("aws") && (
          <NavLink className="menu-item" to={`${prefix}/aws`}>
            <Image src="/assets/images/icons8-amazon-web-services_n.svg" />
            Amazon Web Services
          </NavLink>
        )}

        {allowedClouds.has("gcp") && (
          <NavLink className="menu-item" to={`${prefix}/gcp`}>
            <Image src="/assets/images/icons8-google-cloud-480_n.svg" />
            Google Cloud Platform
          </NavLink>
        )}

        {allowedClouds.has("azure") && (
          <NavLink className="menu-item" to={`${prefix}/azure`}>
            <Image src="/assets/images/icons8-azure_n.svg" />
            Microsoft Azure
          </NavLink>
        )}
      </div>
    </Drawer>
  );
}
